// @ts-nocheck

import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import styled, { css } from 'styled-components';

import { __ } from '../../i18n/l10n';
import LineUpOpenViewButtonContainer from '../../modules/line-up/components/LineUpOpenViewButton/LineUpOpenViewButtonContainer';
import theme from '../../styled-components/dark';
import { BetGroupLabel, BetGroupWrapper } from '../../styled-components/shared';
import type { PresentationItem } from '../../types/betGroup';
import { type BetGroupPlayer, PresentationModes } from '../../types/betGroup';
import { formatSpecifier, getStartPosition } from '../../utils/sliderUtils';
import BetCurrentTotalTagContainer from '../BetCurrentTotalTag/BetCurrentTotalTagContainer';
import LabelWithLine, { BetGroupLabelWithLineClassName } from '../BetGroupLabelWithLine';
import Lines from '../BetGroupLines';
import BetGroupMultilineHeader from '../BetGroupMultilineHeader';
import BetGroupOutcomeContainer from '../BetGroupOutcome/BetGroupOutcomeContainer';
import FlagIcon from '../FlagIcon';
import PlayerWidgetButton from '../PlayerWidgetButton';
import Slider from '../Slider';
import SliderLine from '../SliderLine';

const Wrapper = styled.div``;

const LinesWrapper = styled.div`
  margin: 0.6em;
`;

const Line = styled.div`
  margin: 0.6em 0;

  &:first-child {
    margin-top: 0;
  }

  &:last-child {
    margin-bottom: 0;
  }
`;

const StyledLabelWrapper = styled.div`
  margin: ${({ $playerTrackerInfos }) => ($playerTrackerInfos ? '0.4em 0.5em 0.4em 0' : '0.4em 0')};
  color: ${theme.BetGroupOutcome.color};
  flex-grow: 1;

  display: flex;
  align-items: center;
  justify-content: center;
`;

const sharedLabelStyle = css`
  display: inline-block;
`;

const StyledLabel = styled.div`
  padding-right: 0.3em;

  ${sharedLabelStyle}
`;

const StyledLine = styled.div`
  height: 1px;
  flex-grow: 10000;
  background-color: ${theme.BetGroupOutcome.lineColor};
`;

const StyledCompetitorLabel = styled(BetGroupLabel)`
  margin: 0;
  padding: 0;

  ${sharedLabelStyle}
`;

const StyledLabelWithLine = styled(LabelWithLine)`
  margin: ${({ $playerTrackerInfos }) => ($playerTrackerInfos ? '0.4em 0.5em 0.4em 0' : '0.4em 0')};
  color: ${theme.BetGroupOutcome.color};
  flex-grow: 1;
`;

const StyledLines = styled(Lines)`
  && {
    padding: 0;
  }
`;

const PlayerWidgetFlexWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-direction: row;
  height: 100%;
`;

const Starter = styled.div`
  color: ${theme.BetGroupOutcome.starterColor};
  margin: 0 0.5em;
`;

const FlagLabelWrapper = styled.div`
  display: flex;
  align-items: center;
  margin-left: 0.6em;
`;

const StyledFlagIcon = styled(FlagIcon)`
  width: 1.3em;
  height: 1.3em;
  border-radius: 0.3em;
  margin-right: 0.6em;
`;

const StyledGroupLabel = styled(BetGroupLabel)`
  margin: 0.6em 0;
`;

const StyledBetGroupOutcomeContainer = styled(BetGroupOutcomeContainer)`
  position: relative;
  width: 100%;
  padding-right: 0.4em;
  display: inline-block;

  max-width: 33.333%;

  :nth-child(n + 3) {
    padding-right: 0;
  }
`;

const StyledSlider = styled(Slider)`
  width: 100%;
  font-size: 90%;
`;

const FullWidthSlider = styled(StyledSlider)`
  margin-top: 1.6em;
`;

const StyledLineUpOpenViewButtonContainer = styled(LineUpOpenViewButtonContainer)`
  margin-right: 0.5em;

  &:only-child {
    margin-left: auto;
  }
`;

const FullSlider = ({ items, betTemplate, hasStarters, betPresentationMode }) => {
  const [betIndex, setBetIndex] = useState(getStartPosition(items.length, betPresentationMode.defaultPosition));
  const [item, setItem] = useState(items[betIndex]);

  const onValueChange = useCallback((v) => {
    setBetIndex(v);
  }, []);

  useEffect(() => {
    // $FlowFixMe
    setItem(items[betIndex]);
  }, [items, betIndex]);

  const formatLabel = useCallback(
    () => formatSpecifier(item?.outcomes?.[0].specialBetValue, betPresentationMode.labelSpecifier),
    [betPresentationMode.labelSpecifier, item],
  );

  if (!item) {
    return null;
  }

  const { label, starter, outcomes, playerTrackerInfos } = item;
  const disabled = items.length === 1;

  return (
    <>
      <Line>
        {label ? (
          <PlayerWidgetFlexWrapper>
            <StyledLabelWithLine label={label} $playerTrackerInfos={playerTrackerInfos} />
            {starter && hasStarters ? <Starter>{__('Starter')}</Starter> : null}
            {playerTrackerInfos ? (
              <PlayerWidgetButton
                playerTeam={playerTrackerInfos.playerTeam}
                playerSrId={playerTrackerInfos.playerSrId}
                matchId={playerTrackerInfos.matchId}
              />
            ) : null}
          </PlayerWidgetFlexWrapper>
        ) : null}
        <StyledLines items={outcomes} showPercentDistribution={false} betTemplate={betTemplate} />
      </Line>
      <FullWidthSlider
        step={1}
        min={0}
        max={disabled ? 2 : items.length - 1}
        formatTooltipLabel={formatLabel}
        value={betIndex}
        onChange={onValueChange}
        hideAnchors
        disabled={items.length === 1}
      />
    </>
  );
};

const StyledBetCurrentTotalTagContainer = styled(BetCurrentTotalTagContainer)`
  margin-right: 0.5em;
`;

const LabelWrapper = styled.div`
  margin-right: 0.4em;
`;

type BetGroupMultilineProps = {
  className?: string;
  items: Array<BetGroupPlayer>;
  headers: Array<string>;
  groupLabel: string | null;
  showPercentDistribution: boolean;
  betTemplate: string;
  teamId?: number;
  matchId: number;
  flag: string;
  hasStarters: boolean;
  betPresentationMode: PresentationItem;
  showLineUpButton?: boolean;
};

const BetGroupMultiline = ({
  className,
  items = [],
  headers = [],
  groupLabel,
  showPercentDistribution,
  betTemplate,
  teamId,
  matchId,
  flag,
  hasStarters,
  betPresentationMode,
  showLineUpButton = false,
}: BetGroupMultilineProps) => {
  const labelWrapperNodesRef = useRef<Array<HTMLDivElement | null>>([]);
  const wrapperNodeRef = useRef<HTMLDivElement | null>(null);
  const defaultMarketId = useMemo(() => items[0].outcomes[0].marketId, [items]);
  const defaultSBV = useMemo(() => items[0].outcomes[0].specialBetValue, [items]);
  const displayLabel = useMemo(() => items.length > 0, [items]);

  useLayoutEffect(() => {
    const wrapperNode = wrapperNodeRef.current;
    const labelWrapperNodes = labelWrapperNodesRef.current;

    if (wrapperNode && labelWrapperNodes.length) {
      const { width: wrapperNodeWidth } = wrapperNode.getBoundingClientRect();

      for (const labelWrapperNode of labelWrapperNodes) {
        if (labelWrapperNode) {
          const { width: labelWrapperNodeWidth } = labelWrapperNode.getBoundingClientRect();

          // If the label is too long, we display it on multiple lines and maintain the line separator
          if (labelWrapperNodeWidth > wrapperNodeWidth - 150) {
            labelWrapperNode.style.display = 'flex';
            labelWrapperNode.style.flexDirection = 'column';
          } else {
            labelWrapperNode.style.display = 'inline-block';
            labelWrapperNode.style.flexDirection = '';
          }
        }
      }
    }
  }, []);

  return (
    <Wrapper className={className} ref={wrapperNodeRef}>
      {displayLabel ? <BetGroupMultilineHeader labels={headers} /> : null}
      {groupLabel || teamId ? (
        <BetGroupWrapper>
          <FlagLabelWrapper>
            {flag ? <StyledFlagIcon flagId={flag} /> : null}
            {groupLabel ? <StyledGroupLabel>{groupLabel}</StyledGroupLabel> : null}
          </FlagLabelWrapper>
          {showLineUpButton ? (
            <StyledLineUpOpenViewButtonContainer
              teamId={teamId}
              marketId={defaultMarketId}
              specialBetValue={defaultSBV}
              matchId={matchId}
            />
          ) : null}
        </BetGroupWrapper>
      ) : null}
      <LinesWrapper>
        {betPresentationMode?.type === PresentationModes.SLIDER_BY_BET ? (
          <FullSlider
            items={items}
            showPercentDistribution={showPercentDistribution}
            betTemplate={betTemplate}
            hasStarters={hasStarters}
            betPresentationMode={betPresentationMode}
          />
        ) : (
          items.map(
            (
              {
                label,
                playerFlag,
                subLabel,
                starter,
                outcomes,
                showPercentDistribution: itemShowPercentDistribution,
                playerTrackerInfos,
              },
              index,
            ) => {
              const betId = outcomes[0].betId;

              return (
                <Line key={`betgroup-${label || ''}-${index}`}>
                  {label ? (
                    <PlayerWidgetFlexWrapper>
                      <StyledLabelWrapper $playerTrackerInfos={playerTrackerInfos}>
                        {playerFlag ? <StyledFlagIcon flagId={playerFlag} /> : null}
                        <LabelWrapper ref={(ref) => (labelWrapperNodesRef.current[index] = ref)}>
                          <StyledLabel>{label}</StyledLabel>
                          {subLabel ? <StyledCompetitorLabel>{subLabel}</StyledCompetitorLabel> : null}
                        </LabelWrapper>

                        <StyledLine className={BetGroupLabelWithLineClassName} />
                      </StyledLabelWrapper>
                      {starter && hasStarters ? <Starter>{__('Starter')}</Starter> : null}
                      <StyledBetCurrentTotalTagContainer betId={betId} isFromPlayerRow />
                      {playerTrackerInfos ? (
                        <PlayerWidgetButton
                          playerTeam={playerTrackerInfos.playerTeam}
                          playerSrId={playerTrackerInfos.playerSrId}
                          matchId={playerTrackerInfos.matchId}
                        />
                      ) : null}
                    </PlayerWidgetFlexWrapper>
                  ) : null}
                  {betPresentationMode?.type === PresentationModes.SLIDER_BY_OUTCOME ? (
                    <SliderLine
                      outcomes={outcomes}
                      betTemplate={betTemplate}
                      betPresentationMode={betPresentationMode}
                      BetGroupComponent={StyledBetGroupOutcomeContainer}
                    />
                  ) : (
                    <StyledLines
                      displayLabel={displayLabel}
                      items={outcomes}
                      showPercentDistribution={showPercentDistribution || itemShowPercentDistribution}
                      betTemplate={betTemplate}
                    />
                  )}
                </Line>
              );
            },
          )
        )}
      </LinesWrapper>
    </Wrapper>
  );
};

export default BetGroupMultiline;
